// Deterministic world-space ground scatter — grass tufts and small rocks — the pure,
// GPU-free seam behind two playtest findings (field-ground-scatter): "the background
// should move with drag" (positions are plain world coordinates the draw body projects
// through `worldToScreen` exactly like a bee, so they pan/zoom with the camera) and "the
// background shouldn't be tiled" (a flat season fill replaces the tiled ground; these
// items are the wayfinding landmarks that read against it). No PixiJS import — the actual
// sprite draw lives in `renderer.ts`, exercised on-device only (repo convention D-086) —
// mirroring how `season-ground.ts`, `draw-list.ts`, `facing.ts`, and `blob-colony.ts` stay
// Pixi-free and unit-tested without a GPU.
//
// Positions are generated per spatial-grid cell from a seeded PRNG (never `Math.random`),
// so a viewport's realised scatter is stable across frames, resizes, and reloads: the same
// seed and the same cell always yield the same items, in the same order. The field is
// 4096×4096 world units (DESIGN §14) — far too much to instantiate every frame — so
// `scatterInViewport` only realises the handful of cells overlapping the current viewport
// (plus a margin), a coarser echo of the sim's own uniform spatial grid (DESIGN §7), sized
// for a wayfinding decoration rather than a physics query.

import { SCATTER_SIZE_MULTIPLIER, SPRITE_NATIVE_ZOOM } from './projection';
import type { AssetKey } from './assets';

export type ScatterKind = 'tuft' | 'rock';

/** One deterministic scatter item: a stable `key` (unique across the whole field for a
 * given seed, independent of which viewport query realised it — so a panning viewport
 * never re-keys an item still in view, D-panning-continuity), its world position, and the
 * cosmetic sprite it draws. */
export interface ScatterItem {
  readonly key: string;
  readonly kind: ScatterKind;
  readonly position: readonly [number, number];
  readonly sheet: AssetKey;
}

/** World-space side length of one scatter bucket cell — coarser than the sim's own
 * proximity grid (40 u at seed balance values, DESIGN §7), since scatter is a wayfinding
 * decoration, not a physics query: a handful of these cells cover a typical viewport. */
export const SCATTER_CELL_SIZE = 256;

/** Scatter items generated per cell — a named density constant (field-ground-scatter),
 * tuned so the field reads as navigable landmarks rather than visual noise: sparse enough
 * that individual tufts/rocks are legible at the close-tier zoom, dense enough that a
 * wide-zoom view still reads as a dressed field rather than an empty one. */
export const SCATTER_ITEMS_PER_CELL = 3;

/** Viewport-culling margin (world units) added on every side of the visible world rect
 * (field-ground-scatter): realises an item slightly before it enters view so a sprite
 * that straddles the viewport edge does not visibly pop in. */
export const SCATTER_VIEWPORT_MARGIN = 64;

/** The nominal on-screen edge (px) of a scatter sprite at camera scale 1 — the authored
 * art size (`tools/gen-ground-scatter-sprites.mjs`) times its world-size multiplier, since
 * scatter is drawn world-anchored like every other sprite (`spriteCameraScale`). Used only
 * to decide when a sprite has shrunk past legibility, never to size the draw. */
const SCATTER_SPRITE_PX = 16 * (SCATTER_SIZE_MULTIPLIER / SPRITE_NATIVE_ZOOM);

/** Below this on-screen sprite edge (px) scatter reads as speckled noise rather than
 * landmarks, so it is suppressed outright. At the colony zoom floor (0.1×) a sprite is
 * ~1.6 px — the wallpaper effect this threshold exists to kill. */
const SCATTER_MIN_LEGIBLE_PX = 5;

/** How many items per cell to realise at a given camera scale (playtest follow-up).
 *
 * Scatter density is authored for the close-tier view. Zooming out multiplies the visible
 * world area by 1/scale², so holding the per-cell count fixed carpets the screen: at the
 * 0.1× colony floor the field renders as uniform speckle, which is the opposite of the
 * wayfinding this decoration exists for. Thinning by `scale` keeps the *on-screen* landmark
 * count roughly stable as the camera pulls back, and cuts to zero once a sprite would be
 * sub-legible ({@link SCATTER_MIN_LEGIBLE_PX}).
 *
 * Thinning takes a **prefix** of each cell's fixed-order item list, so an item that survives
 * a detail drop keeps its identity and `key` — zooming never re-keys a sprite still on
 * screen, it only adds or removes ones at the tail. Monotonically non-increasing in
 * `scale`, so there is no density flicker across a zoom sweep. */
export function scatterDetailForScale(scale: number): number {
  if (!Number.isFinite(scale) || scale <= 0) return 0;
  if (scale * SCATTER_SPRITE_PX < SCATTER_MIN_LEGIBLE_PX) return 0;
  if (scale >= 1) return SCATTER_ITEMS_PER_CELL;
  return Math.max(1, Math.ceil(SCATTER_ITEMS_PER_CELL * scale));
}

/** The fixed, cosmetic scatter seed the production renderer wires in. Scatter is a
 * client-only visual dressing with no wire field (like flower colour variety in
 * `season-ground.ts`) — there is no per-match seed on the client to derive it from, so this
 * is a constant, not a balance/match value. Exported so tests exercise the exact production
 * field rather than an arbitrary stand-in. */
export const DEFAULT_SCATTER_SEED = 0x5ca77e2d;

/** The two cosmetic sprite variants per kind (field-ground-scatter) — enough visual variety
 * that repeated tufts/rocks don't read as an obvious stamp, without the asset-authoring cost
 * of more. */
const TUFT_SHEETS: readonly AssetKey[] = ['grassTuftA', 'grassTuftB'];
const ROCK_SHEETS: readonly AssetKey[] = ['rockA', 'rockB'];

/** The fraction of items in a cell that are rocks (the remainder are tufts): rocks read as
 * the rarer, more distinctive landmark; tufts are the common ground dressing. */
const ROCK_FRACTION = 0.25;

// splitmix32: a small, fast, well-distributed seeded PRNG (never `Math.random` —
// DESIGN §7's determinism rule extends to this client-cosmetic generator on principle, even
// though it never touches the sim). Mirrors the avalanche-mixer shape of `season-ground.ts`'s
// `fmix32`, extended into a repeatable stream by advancing a running state each call.
function splitmix32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x9e3779b9) >>> 0;
    let z = state;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
    z = (z ^ (z >>> 15)) >>> 0;
    return z / 4294967296; // -> [0, 1)
  };
}

/** Mixes the world seed with a cell's integer grid coordinates into one 32-bit per-cell
 * seed, so every cell gets an independent-looking but fully reproducible PRNG stream —
 * negative cell coordinates (the field is centred on the origin, DESIGN §14) mix in the
 * same way as positive ones since the XOR/multiply chain only ever sees the coordinate's
 * bit pattern. */
function cellSeed(worldSeed: number, cellX: number, cellY: number): number {
  let h = worldSeed >>> 0;
  h = Math.imul(h ^ cellX, 0x27d4eb2f) >>> 0;
  h = Math.imul(h ^ cellY, 0x165667b1) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h;
}

/** The deterministic scatter items in one grid cell, addressed by its integer cell
 * coordinates (world position `cellX * SCATTER_CELL_SIZE` is the cell's min corner, not a
 * world position itself). Pure: the same `(worldSeed, cellX, cellY)` always yields the same
 * array — same length, same order, same values — so it is safe to regenerate every frame
 * rather than caching (the caller is free to cache by cell key if profiling ever demands
 * it). */
export function scatterForCell(
  worldSeed: number,
  cellX: number,
  cellY: number,
  itemsPerCell: number = SCATTER_ITEMS_PER_CELL,
): ScatterItem[] {
  const rand = splitmix32(cellSeed(worldSeed, cellX, cellY));
  const originX = cellX * SCATTER_CELL_SIZE;
  const originY = cellY * SCATTER_CELL_SIZE;
  const items: ScatterItem[] = [];
  // A prefix of the full fixed-order list, never a re-draw: the PRNG stream is advanced in
  // the same order regardless of `itemsPerCell`, so item `i` is identical at every detail
  // level and a zoom-driven thin/thicken only trims or extends the tail.
  const count = Math.max(0, Math.min(SCATTER_ITEMS_PER_CELL, Math.floor(itemsPerCell)));
  for (let i = 0; i < count; i++) {
    const x = originX + rand() * SCATTER_CELL_SIZE;
    const y = originY + rand() * SCATTER_CELL_SIZE;
    const isRock = rand() < ROCK_FRACTION;
    const variant = rand() < 0.5 ? 0 : 1;
    const kind: ScatterKind = isRock ? 'rock' : 'tuft';
    const sheet = isRock ? ROCK_SHEETS[variant] : TUFT_SHEETS[variant];
    items.push({ key: `${cellX},${cellY},${i}`, kind, position: [x, y], sheet });
  }
  return items;
}

/** A world-space axis-aligned rectangle — the visible world rect the draw body derives
 * from the camera + viewport pixel size via `screenToWorld` at two opposite corners. */
export interface WorldRect {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/** Half the DESIGN §14 seed field size (4096 world units), centred at the origin — the same
 * coordinate frame `projection.ts`'s `ESTABLISHING_CAMERA`/the wire's u16 quantization
 * contract assume. Scatter has no wire field-size of its own (a client-only cosmetic layer,
 * like its seed) — this mirrors the constant rather than deriving it from a manifest
 * (field-ground-scatter review finding: an unclamped viewport query, reachable at the
 * camera's real minimum zoom, enumerated thousands of cells and invented landmarks beyond
 * this edge). */
export const SCATTER_FIELD_HALF_SIZE = 2048;

// `deathmatch-world` D-6: this constant is now the **fallback**, not the rule. `scatterInViewport`
// takes the half-extent as an argument, and the renderer passes the manifest-derived play
// boundary (`play-boundary.ts`) — because on a world smaller than the seed field, decor sprayed
// to ±2048 lands far outside the boundary bees stop at, which was invisible until a line was
// drawn there and obvious the moment one was.

/** The ground dressing spans the complete playable square. Kept as a pure seam so renderer wiring
 * and field-boundary behavior can be checked without constructing a GPU context. */
export function scatterHalfForBoundary(boundaryHalf: number): number {
  return boundaryHalf;
}

/** Every scatter item whose position falls within `viewport` expanded by
 * `SCATTER_VIEWPORT_MARGIN` on every side, and CLAMPED to the field bounds
 * (`±SCATTER_FIELD_HALF_SIZE`, field-ground-scatter's viewport culling): only the grid cells
 * overlapping that clamped, expanded viewport are realised — never the whole 4096×4096 field,
 * and never anything beyond it either. The unclamped rect a wide-zoomed-out camera unprojects
 * can be many times larger than the field itself (at the camera's 0.1x minimum zoom, a
 * 1920x1080 viewport unprojects to roughly 19200x15000 world units); clamping first keeps the
 * enumerated cell count bounded by the field regardless of how far out the camera pulls. An
 * item's `key` is derived purely from its owning cell + index (`scatterForCell`), never from
 * the viewport itself, so the same item keeps the same key across successive queries as the
 * viewport pans past it (no flicker/re-creation). Pure and cheap enough to call once per
 * frame; the caller may still cache by cell if profiling ever demands it. */
export function scatterInViewport(
  worldSeed: number,
  viewport: WorldRect,
  itemsPerCell: number = SCATTER_ITEMS_PER_CELL,
  fieldHalfSize: number = SCATTER_FIELD_HALF_SIZE,
): ScatterItem[] {
  if (itemsPerCell <= 0) return [];
  const minX = Math.max(viewport.minX - SCATTER_VIEWPORT_MARGIN, -fieldHalfSize);
  const minY = Math.max(viewport.minY - SCATTER_VIEWPORT_MARGIN, -fieldHalfSize);
  const maxX = Math.min(viewport.maxX + SCATTER_VIEWPORT_MARGIN, fieldHalfSize);
  const maxY = Math.min(viewport.maxY + SCATTER_VIEWPORT_MARGIN, fieldHalfSize);
  if (minX > maxX || minY > maxY) return []; // the requested rect lies entirely off-field
  const cellMinX = Math.floor(minX / SCATTER_CELL_SIZE);
  const cellMinY = Math.floor(minY / SCATTER_CELL_SIZE);
  const cellMaxX = Math.floor(maxX / SCATTER_CELL_SIZE);
  const cellMaxY = Math.floor(maxY / SCATTER_CELL_SIZE);

  const items: ScatterItem[] = [];
  for (let cy = cellMinY; cy <= cellMaxY; cy++) {
    for (let cx = cellMinX; cx <= cellMaxX; cx++) {
      for (const item of scatterForCell(worldSeed, cx, cy, itemsPerCell)) {
        const [x, y] = item.position;
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
          items.push(item);
        }
      }
    }
  }
  return items;
}
